import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { StyledCopyButton } from "./index.styled";
import CopyIcon from "../CopyIcon";
import { colors } from "../../../styles/colors";
import { createShortLinkAction } from "../../../store/dashboardSlice";
import { getConfig, type AppConfig } from "../../../utils/config";
import type { RootState, AppDispatch } from "../../../store";

interface CopyButtonProps {
  shortLink?: string;
  longLink: string;
  triggerId?: number;
}

const CopyButton = ({ shortLink, longLink, triggerId }: CopyButtonProps) => {
  const [copiedShort, setCopiedShort] = useState(false);
  const [copiedLong, setCopiedLong] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isCreatingShortLink, setIsCreatingShortLink] = useState(false);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    getConfig().then(setConfig);
  }, []);

  // Get the current trigger data from the store to check for updated short_link_id
  const currentTrigger = useSelector((state: RootState) =>
    triggerId
      ? state.dashboard.data.triggers.find(
        (trigger) => trigger.ID === triggerId
      )
      : null
  );

  // Use the short link from the store if available, otherwise use the prop
  const actualShortLink = currentTrigger?.short_link_id
    ? `https://wa.me/message/${currentTrigger.short_link_id}`
    : shortLink;

  // Auto-create short link if missing when component mounts
  useEffect(() => {
    if (
      config &&
      triggerId &&
      !actualShortLink &&
      !isCreatingShortLink &&
      config.SHOW_SHORT_LINK
    ) {
      // No short link exists, create one automatically
      setIsCreatingShortLink(true);
      dispatch(createShortLinkAction(triggerId))
        .unwrap()
        .catch((error) => {
          console.error("Failed to create short link:", error);
          setIsCreatingShortLink(false);
        });
    }
  }, [config, triggerId, actualShortLink, isCreatingShortLink, dispatch]);

  // Auto-copy the short link when it becomes available after creation
  useEffect(() => {
    if (actualShortLink && isCreatingShortLink) {
      // Short link was just created, copy it automatically
      navigator.clipboard
        .writeText(actualShortLink)
        .then(() => {
          setCopiedShort(true);
          setTimeout(() => setCopiedShort(false), 2000);
          setIsCreatingShortLink(false);
        })
        .catch((err) => {
          console.error("Failed to copy short link:", err);
          setIsCreatingShortLink(false);
        });
    }
  }, [actualShortLink, isCreatingShortLink]);

  const showToastMessage = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  useEffect(() => {
    if (showToast) {
      // Create toast element and append to body
      const toastElement = document.createElement("div");
      toastElement.className = "toast-fixed";
      toastElement.textContent = "לא קיים קישור מקוצר עבור זה";
      toastElement.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #ff3b30;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        z-index: 9999;
        box-shadow: 0px 4px 20px rgba(255, 59, 48, 0.4);
        animation: slideUp 0.3s ease-out;
        pointer-events: none;
        min-width: 200px;
        text-align: center;
      `;

      // Add animation keyframes if not already added
      if (!document.querySelector("#toast-styles")) {
        const style = document.createElement("style");
        style.id = "toast-styles";
        style.textContent = `
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `;
        document.head.appendChild(style);
      }

      document.body.appendChild(toastElement);

      // Remove toast after 3 seconds
      setTimeout(() => {
        if (toastElement.parentNode) {
          toastElement.parentNode.removeChild(toastElement);
        }
      }, 3000);
    }
  }, [showToast]);

  const handleCopyShortLink = async () => {
    if (!config?.SHOW_SHORT_LINK) {
      // Short links are disabled, don't do anything
      return;
    }

    if (!actualShortLink) {
      // If still no short link exists, show error message
      showToastMessage();
      return;
    }
    try {
      await navigator.clipboard.writeText(actualShortLink);
      setCopiedShort(true);
      setTimeout(() => setCopiedShort(false), 2000);
    } catch (err) {
      console.error("Failed to copy short link:", err);
    }
  };

  const handleCopyLongLink = async () => {
    try {
      await navigator.clipboard.writeText(longLink);
      setCopiedLong(true);
      setTimeout(() => setCopiedLong(false), 2000);
    } catch (err) {
      console.error("Failed to copy long link:", err);
    }
  };

  return (
    <StyledCopyButton>
      <div
        className={`copy-button long-link ${copiedLong ? "copied" : ""}`}
        onClick={handleCopyLongLink}
      >
        {copiedLong ? (
          <Check color={colors.blue} size={20} strokeWidth={2.5} />
        ) : (
          <CopyIcon color={colors.blue} width={19} height={22} />
        )}
      </div>
      {config?.SHOW_SHORT_LINK && (
        <div
          className={`copy-button short-link ${copiedShort ? "copied" : ""} ${isCreatingShortLink ? "loading" : ""
            }`}
          onClick={handleCopyShortLink}
          title={actualShortLink ? "העתק קישור מקוצר" : "צור קישור מקוצר"}
        >
          {isCreatingShortLink ? (
            <div className="loading-spinner" />
          ) : copiedShort ? (
            <Check color={colors.green} size={20} strokeWidth={2.5} />
          ) : (
            <CopyIcon color={colors.green} width={19} height={22} />
          )}
        </div>
      )}
    </StyledCopyButton>
  );
};

export default CopyButton;
